import React, { useState, useEffect } from "react";
import API from "../services/api";
import { useIsMobile } from "../hooks/useIsMobile";
import { generateNotePDF, generateROIPDF, generateSchoolConsentPDF } from "./notePdfGenerator";
import styles from "./styles";
import { computeAge, formatDate, determineFormType } from "./utils";

// Import components
import { SOAPNewModal, SOAPViewModal } from "./SOAPComponents";
import { TAContractModal, TAContractViewModal } from "./TAContractComponents";
import { ProfileModal, ClientFormModal, GenerateIntakeLinkModal } from "./ClientComponents";
import { IntakeAssessmentModal, IntakeAssessmentViewModal } from "./IntakeAssessmentComponents";
import { HandwrittenNoteModal, HandwrittenNoteViewModal } from "./HandwrittenNoteComponents";
import { CollateralContactModal, CollateralContactViewModal } from "./CollateralContactComponents";
import { SchoolNoteModal, SchoolNoteViewModal } from "./SchoolNoteComponents";
import { GenerateROIModal, ViewROISubmissionModal } from "./ROIComponents";
import { SendSchoolConsentModal, ViewSchoolConsentModal } from "./SchoolConsentComponents";

/* ------------------------------------------
   MAIN PAGE for SOAP + client display
   ============================================== */
function TherapistSOAPNotesPage() {
  const isMobile = useIsMobile();
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [selectedClient, setSelectedClient] = useState(null); // minimal client {id, client_name}
  const [selectedClientDetails, setSelectedClientDetails] = useState(null); // full details from GET /clients/:id

  const [clientNotes, setClientNotes] = useState([]);
  const [clientForms, setClientForms] = useState([]); // Store client intake forms
  const [roiSubmissions, setRoiSubmissions] = useState([]); // Store ROI submissions
  const [schoolConsentSubmissions, setSchoolConsentSubmissions] = useState([]); // School consent submissions
  const [loading, setLoading] = useState(false);

  // For creating new note
  const [showNewModal, setShowNewModal] = useState(false);
  // For viewing/editing existing note
  const [viewingNote, setViewingNote] = useState(null);
  // For viewing profile
  const [showProfileModal, setShowProfileModal] = useState(false);
  // For viewing client forms
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  // For generating intake link
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
  // For creating new intake assessment
  const [showIntakeAssessmentModal, setShowIntakeAssessmentModal] = useState(false);
  // For creating new handwritten note
  const [showHandwrittenNoteModal, setShowHandwrittenNoteModal] = useState(false);
  // For creating new collateral contact note
  const [showCollateralContactModal, setShowCollateralContactModal] = useState(false);
  // For creating new school note
  const [showSchoolNoteModal, setShowSchoolNoteModal] = useState(false);
  // ROI-related state
  const [showGenerateROIModal, setShowGenerateROIModal] = useState(false);
  const [showViewROIModal, setShowViewROIModal] = useState(false);
  const [selectedROISubmission, setSelectedROISubmission] = useState(null);
  // School consent form state
  const [showSendSchoolConsentModal, setShowSendSchoolConsentModal] = useState(false);
  const [showViewSchoolConsentModal, setShowViewSchoolConsentModal] = useState(false);
  const [selectedSchoolConsent, setSelectedSchoolConsent] = useState(null);
  // Welcome email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  // Send note/ROI as PDF email state
  const [showPdfEmailModal, setShowPdfEmailModal] = useState(false);
  const [pdfEmailTo, setPdfEmailTo] = useState("");
  const [pdfEmailNote, setPdfEmailNote] = useState(null);
  const [pdfEmailRoi, setPdfEmailRoi] = useState(null);
  const [pdfEmailSchoolConsent, setPdfEmailSchoolConsent] = useState(null);
  const [pdfEmailNoteType, setPdfEmailNoteType] = useState("");
  const [pdfEmailSending, setPdfEmailSending] = useState(false);
  const [pdfEmailStatus, setPdfEmailStatus] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  /* ------------------------------------------
     1) FETCH THERAPIST CLIENTS
  ------------------------------------------ */
  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await API.get("/therapist/clients");
      setClients(res.data);
    } catch (err) {
      console.error(err);
      alert("Error fetching clients");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------
     2) FETCH ROI SUBMISSIONS
  ------------------------------------------ */
  const fetchROISubmissions = async (clientId) => {
    try {
      const response = await API.get(`/forms/therapist/roi-submissions/${clientId}`);
      setRoiSubmissions(response.data || []);
    } catch (error) {
      console.error('Error fetching ROI submissions:', error);
      setRoiSubmissions([]);
    }
  };

  const fetchSchoolConsentSubmissions = async (clientId) => {
    try {
      const response = await API.get(`/forms/therapist/school-consent-submissions/${clientId}`);
      setSchoolConsentSubmissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching school consent submissions:', error);
      setSchoolConsentSubmissions([]);
    }
  };

  /* ------------------------------------------
     3) SELECT CLIENT => GET EXTENDED DETAILS
  ------------------------------------------ */
  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setViewingNote(null);
    setSelectedClientDetails(null);
    setClientNotes([]);
    setClientForms([]); // Clear forms when changing clients
    setRoiSubmissions([]); // Clear ROI submissions when changing clients
    setSchoolConsentSubmissions([]);

    if (!client?.id) return;

    setLoading(true);
    try {
      // 2a) fetch extended details (dob, gender, reason for visit, etc.)
      const detailRes = await API.get(`/therapist/clients/${client.id}`);
      setSelectedClientDetails(detailRes.data);
      
      // 2b) fetch their SOAP notes
      const notesRes = await API.get(`/therapist/notes?clientId=${client.id}`);
      setClientNotes(notesRes.data);
      
      // 2c) fetch client intake forms
      const formsRes = await API.get(`/forms/therapist/intake-data/${client.id}`);
      setClientForms(formsRes.data || []);
      
      // 2d) fetch ROI submissions
      await fetchROISubmissions(client.id);

      // 2e) fetch school consent submissions
      await fetchSchoolConsentSubmissions(client.id);
    } catch (error) {
      console.error(error);
      alert("Error fetching client data, notes, forms, or ROI submissions");
    } finally {
      setLoading(false);
    }
  };

  // On mobile, return from a client's notes back to the searchable list
  const handleBackToClients = () => {
    setSelectedClient(null);
    setSelectedClientDetails(null);
    setViewingNote(null);
    setClientNotes([]);
    setClientForms([]);
    setRoiSubmissions([]);
  };

  // Filtered client list by search
  const filteredClients = clients.filter((c) =>
    c.client_name.toLowerCase().includes(searchText.toLowerCase())
  );

  /* ------------------------------------------
     4) CREATE NEW NOTE (SOAP, TA, INTAKE ASSESSMENT, HANDWRITTEN, COLLATERAL CONTACT, SCHOOL NOTE)
  ------------------------------------------ */
  const createNote = async (noteData) => {
    if (!selectedClient) return;
    const notesPayload = JSON.stringify(noteData);

    setLoading(true);
    try {
      await API.post("/therapist/notes", {
        clientId: selectedClient.id,
        notes: notesPayload
      });
      
      let successMessage = "Note created!";
      if (noteData?.type === "TA Contract") {
        successMessage = "TA Contract created!";
      } else if (noteData?.type === "IntakeAssessment") {
        successMessage = "Intake Assessment created!";
      } else if (noteData?.type === "HandwrittenNote") {
        successMessage = "Handwritten note created!";
      } else if (noteData?.type === "CollateralContact") {
        successMessage = "Collateral Contact note created!";
      } else if (noteData?.type === "SchoolNote") {
        successMessage = "School Note created!";
      }
      
      alert(successMessage);
      
      // Close all modals
      setShowNewModal(false);
      setShowIntakeAssessmentModal(false);
      setShowHandwrittenNoteModal(false);
      setShowCollateralContactModal(false);
      setShowSchoolNoteModal(false);

      // Refresh notes
      const refreshed = await API.get(`/therapist/notes?clientId=${selectedClient.id}`);
      setClientNotes(refreshed.data);
    } catch (err) {
      console.error(err);
      alert("Error creating note");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------
     5) VIEW / EDIT EXISTING NOTE
  ------------------------------------------ */
  const viewNote = (note) => {
    setViewingNote(note);
  };

  const updateSOAPNote = async (noteId, updatedNote) => {
    setLoading(true);
    try {
      const notesPayload = JSON.stringify(updatedNote);
      await API.put(`/therapist/notes/${noteId}`, {
        notes: notesPayload
      });
      alert("Note updated!");
      setViewingNote(null);

      // Refresh notes
      const refreshed = await API.get(`/therapist/notes?clientId=${selectedClient.id}`);
      setClientNotes(refreshed.data);
    } catch (err) {
      console.error(err);
      alert("Error updating note");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------
     6) PROFILE MODAL
  ------------------------------------------ */
  const openProfileModal = () => setShowProfileModal(true);
  const closeProfileModal = () => setShowProfileModal(false);

  /* ------------------------------------------
     7) CLIENT FORMS
  ------------------------------------------ */
  // View client form
  const handleViewForm = (form) => {
    setSelectedForm(form);
    setShowFormModal(true);
  };

  /* ------------------------------------------
     8) GENERATE INTAKE LINK
  ------------------------------------------ */
  const openGenerateLinkModal = () => {
    if (!selectedClient) {
      alert("Please select a client first");
      return;
    }
    setShowGenerateLinkModal(true);
  };
  
  const closeGenerateLinkModal = () => {
    setShowGenerateLinkModal(false);
  };

  /* ------------------------------------------
     9) ROI HANDLERS
  ------------------------------------------ */
  const openGenerateROIModal = () => {
    if (!selectedClient) {
      alert("Please select a client first");
      return;
    }
    setShowGenerateROIModal(true);
  };

  const closeGenerateROIModal = () => {
    setShowGenerateROIModal(false);
    // Refresh ROI submissions after generating new one
    if (selectedClient?.id) {
      fetchROISubmissions(selectedClient.id);
    }
  };

  const handleViewROISubmission = (submission) => {
    setSelectedROISubmission(submission);
    setShowViewROIModal(true);
  };

  const closeViewROIModal = () => {
    setShowViewROIModal(false);
    setSelectedROISubmission(null);
  };

  /* ------------------------------------------
     9b) SCHOOL CONSENT HANDLERS
  ------------------------------------------ */
  const openSendSchoolConsentModal = () => {
    if (!selectedClient) {
      alert("Please select a client first");
      return;
    }
    setShowSendSchoolConsentModal(true);
  };

  const closeSendSchoolConsentModal = () => {
    setShowSendSchoolConsentModal(false);
    if (selectedClient?.id) {
      fetchSchoolConsentSubmissions(selectedClient.id);
    }
  };

  const handleViewSchoolConsent = (submission) => {
    setSelectedSchoolConsent(submission);
    setShowViewSchoolConsentModal(true);
  };

  const closeViewSchoolConsentModal = () => {
    setShowViewSchoolConsentModal(false);
    setSelectedSchoolConsent(null);
  };

  // Called when user clicks "Save" in profile modal
  const handleSaveProfile = async (updatedFields) => {
    if (!selectedClientDetails) return;

    setLoading(true);
    try {
      await API.put(`/therapist/clients/${selectedClientDetails.id}`, {
        // Updated with new field names for DB
        clientName: updatedFields.clientName,
        phone: updatedFields.phone,
        reasonForVisit: updatedFields.reasonForVisit,
        email: updatedFields.email,
        address: updatedFields.address,
        city: updatedFields.city,
        state: updatedFields.state,
        zipCode: updatedFields.zipCode,
        emergencyContactName: updatedFields.emergencyContactName,
        emergencyContactPhone: updatedFields.emergencyContactPhone,
        gender: updatedFields.gender,
        dob: updatedFields.dob,
        medications: updatedFields.medications
      });
      alert("Profile updated!");
      // Update local state
      setSelectedClientDetails((prev) => ({
        ...prev,
        client_name: updatedFields.clientName,
        phone: updatedFields.phone,
        reason_for_visit: updatedFields.reasonForVisit,
        email: updatedFields.email,
        address: updatedFields.address,
        city: updatedFields.city,
        state: updatedFields.state,
        zip_code: updatedFields.zipCode,
        emergency_contact_name: updatedFields.emergencyContactName,
        emergency_contact_phone: updatedFields.emergencyContactPhone,
        gender: updatedFields.gender,
        dob: updatedFields.dob,
        medications: updatedFields.medications
      }));
      closeProfileModal();
    } catch (err) {
      console.error(err);
      alert("Error updating client profile");
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine note type
  const getNoteType = (note) => {
    try {
      const parsed = JSON.parse(note.notes);
      return parsed.type || "SOAP Note";
    } catch (err) {
      return "SOAP Note";
    }
  };
  
  // FIXED: Format date in a more readable way (prevents timezone issues)
  const formatDOB = (dateString) => {
    if (!dateString) return "Not specified";
    
    try {
      let date;
      
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // For YYYY-MM-DD format, create local date to prevent timezone shift
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date)) return dateString;
      
      return new Intl.DateTimeFormat('en-US', {
        month: 'long', 
        day: 'numeric', 
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };
  
/* ------------------------------------------
     SEND NOTE AS PDF HANDLERS
  ------------------------------------------ */
  const handleSendAsPDF = (note) => {
    let noteType = "SOAP Note";
    try { noteType = JSON.parse(note.notes).type || "SOAP Note"; } catch {}
    if (noteType === "SchoolNote") noteType = "School Note";
    setPdfEmailNote(note);
    setPdfEmailRoi(null);
    setPdfEmailSchoolConsent(null);
    setPdfEmailNoteType(noteType);
    setPdfEmailTo(selectedClientDetails?.email || "");
    setPdfEmailStatus(null);
    setShowPdfEmailModal(true);
  };

  const handleSendROIAsPDF = (submission) => {
    setPdfEmailRoi(submission);
    setPdfEmailNote(null);
    setPdfEmailSchoolConsent(null);
    setPdfEmailNoteType("Release of Information");
    setPdfEmailTo(selectedClientDetails?.email || "");
    setPdfEmailStatus(null);
    setShowPdfEmailModal(true);
  };

  const handleSendSchoolConsentAsPDF = (submission) => {
    setPdfEmailSchoolConsent(submission);
    setPdfEmailRoi(null);
    setPdfEmailNote(null);
    setPdfEmailNoteType("School Consent");
    setPdfEmailTo(selectedClientDetails?.email || "");
    setPdfEmailStatus(null);
    setShowPdfEmailModal(true);
  };

  const handleSubmitPdfEmail = async () => {
    if (!pdfEmailTo) { alert("Please enter a recipient email address."); return; }
    setPdfEmailSending(true);
    setPdfEmailStatus(null);
    try {
      const clientName = selectedClient?.client_name || "Client";
      let result;
      if (pdfEmailSchoolConsent) result = await generateSchoolConsentPDF(pdfEmailSchoolConsent, clientName);
      else if (pdfEmailRoi)      result = generateROIPDF(pdfEmailRoi, clientName);
      else                       result = await generateNotePDF(pdfEmailNote, clientName);
      await API.post("/therapist/send-note-email", {
        recipientEmail: pdfEmailTo,
        pdfBase64: result.pdfBase64,
        fileName: result.fileName,
        noteType: result.noteType,
        clientName,
      });
      setPdfEmailStatus("success");
    } catch (err) {
      console.error("Error sending PDF:", err);
      setPdfEmailStatus("error");
    } finally {
      setPdfEmailSending(false);
    }
  };

/* ------------------------------------------
     WELCOME EMAIL HANDLERS
  ------------------------------------------ */
  const handleOpenEmailModal = async () => {
    if (!selectedClient) {
      alert("Please select a client first");
      return;
    }
    setEmailStatus(null);
    setEmailTo(selectedClientDetails?.email || "");
    try {
      const res = await API.get("/therapist/profile");
      setEmailMessage(res.data.default_welcome_message || "");
    } catch (err) {
      setEmailMessage("");
    }
    setShowEmailModal(true);
  };

  const handleSendWelcomeEmail = async () => {
    if (!emailTo) {
      alert("Please enter a recipient email address.");
      return;
    }
    setEmailSending(true);
    setEmailStatus(null);
    try {
      await API.post("/therapist/send-welcome-email", {
        clientId: selectedClient.id,
        recipientEmail: emailTo,
        message: emailMessage,
      });
      setEmailStatus({ type: "success", text: "Email sent successfully!" });
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send email.";
      setEmailStatus({ type: "error", text: msg });
    } finally {
      setEmailSending(false);
    }
  };

/* ------------------------------------------
     RENDER
  ------------------------------------------ */
  // Responsive layout: on phones, stack vertically and use a "collapse on select"
  // flow — show the client list until one is picked, then show that client's notes.
  const containerStyle = isMobile
    ? { ...enhancedStyles.pageContainer, padding: "12px" }
    : enhancedStyles.pageContainer;
  const titleStyle = isMobile
    ? { ...enhancedStyles.pageTitle, fontSize: "20px", marginBottom: "12px" }
    : enhancedStyles.pageTitle;
  const layoutStyle = isMobile
    ? { ...enhancedStyles.layout, flexDirection: "column", gap: "12px", minHeight: "auto" }
    : enhancedStyles.layout;
  const leftPanelStyle = isMobile
    ? { ...enhancedStyles.leftPanel, width: "100%" }
    : enhancedStyles.leftPanel;
  const rightPanelStyle = isMobile
    ? { ...enhancedStyles.rightPanel, width: "100%" }
    : enhancedStyles.rightPanel;
  const infoGridStyle = isMobile
    ? { ...enhancedStyles.clientInfoGrid, gridTemplateColumns: "1fr" }
    : enhancedStyles.clientInfoGrid;
  const showList = !isMobile || !selectedClient;
  const showDetail = !isMobile || !!selectedClient;

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Session Notes</h1>
      {loading && (
        <div style={enhancedStyles.loadingOverlay}>
          <div style={enhancedStyles.loadingSpinner}></div>
          <p>Loading...</p>
        </div>
      )}

      <div style={layoutStyle}>
        {/* LEFT: Scrollable client list + search */}
        {showList && (
        <div style={leftPanelStyle}>
          <h2 style={enhancedStyles.panelTitle}>Clients</h2>
          <div style={enhancedStyles.searchContainer}>
            <input
              type="text"
              placeholder="Search client..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={enhancedStyles.searchBox}
            />
            <span style={enhancedStyles.searchIcon}>🔍</span>
          </div>
          <div style={enhancedStyles.clientList}>
            {filteredClients.length === 0 ? (
              <div style={enhancedStyles.emptyState}>
                {searchText ? 'No matches found' : 'No clients available'}
              </div>
            ) : (
              filteredClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    ...enhancedStyles.clientItem,
                    backgroundColor:
                      selectedClient?.id === client.id ? "#E2EEFF" : "#fff",
                    borderLeft: 
                      selectedClient?.id === client.id ? "4px solid #1890ff" : "4px solid transparent"
                  }}
                  onClick={() => handleClientSelect(client)}
                >
                  {client.client_name}
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* RIGHT: notes for selected client */}
        {showDetail && (
        <div style={rightPanelStyle}>
          {selectedClient ? (
            <>
              {isMobile && (
                <button
                  style={enhancedStyles.backToClientsButton}
                  onClick={handleBackToClients}
                >
                  ← Back to clients
                </button>
              )}
              <div style={enhancedStyles.clientHeader}>
                <h2 style={enhancedStyles.clientName}>
                  {selectedClient.client_name}
                </h2>
              </div>

              {/* Enhanced client info box */}
              {selectedClientDetails && (
                <div style={enhancedStyles.clientInfoBox}>
                  <div style={infoGridStyle}>
                    <div style={enhancedStyles.clientInfoSection}>
                      <h3 style={enhancedStyles.sectionHeading}>Personal Information</h3>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Date of Birth:</span>
                        <span style={enhancedStyles.infoValue}>
                          {formatDOB(selectedClientDetails.dob)}
                          {selectedClientDetails.dob && (
                            <span style={enhancedStyles.ageIndicator}>
                              (Age: {computeAge(selectedClientDetails.dob)})
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Gender:</span>
                        <span style={enhancedStyles.infoValue}>
                          {selectedClientDetails.gender || "Not specified"}
                        </span>
                      </div>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Phone:</span>
                        <span style={enhancedStyles.infoValue}>
                          {selectedClientDetails.phone || "Not provided"}
                        </span>
                      </div>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Email:</span>
                        <span style={enhancedStyles.infoValue}>
                          {selectedClientDetails.email || "Not provided"}
                        </span>
                      </div>
                    </div>
                    
                    <div style={enhancedStyles.clientInfoSection}>
                      <h3 style={enhancedStyles.sectionHeading}>Clinical Information</h3>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Reason for Visit:</span>
                        <span style={enhancedStyles.infoValue}>
                          {selectedClientDetails.reason_for_visit || "Not specified"}
                        </span>
                      </div>
                      <div style={enhancedStyles.infoRow}>
                        <span style={enhancedStyles.infoLabel}>Diagnosis Code:</span>
                        <span style={enhancedStyles.infoValue}>
                          {selectedClientDetails.diagnosis_code || "Not assigned"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Medications Section */}
                  <div style={enhancedStyles.medicationsSection}>
                    <h3 style={enhancedStyles.sectionHeading}>Current Medications</h3>
                    <div style={enhancedStyles.medicationsList}>
                      {selectedClientDetails.medications ? (
                        <p style={enhancedStyles.medicationsText}>
                          {selectedClientDetails.medications}
                        </p>
                      ) : (
                        <p style={enhancedStyles.noMedications}>
                          No medications recorded
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={enhancedStyles.profileButtonContainer}>
                    <button
                      style={enhancedStyles.profileButton}
                      onClick={openProfileModal}
                    >
                      View / Edit Full Profile
                    </button>
                  </div>
                </div>
              )}

              {/* Main action buttons row - Clinical Documentation */}
              <div style={enhancedStyles.actionCategory}>
                <h3 style={enhancedStyles.actionCategoryTitle}>Clinical Documentation</h3>
                <div style={enhancedStyles.actionButtonsRow}>
                  <button
                    style={enhancedStyles.primaryButton}
                    onClick={() => setShowNewModal("soap")}
                  >
                    + New SOAP Note
                  </button>

                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--primary-color)" }}
                    onClick={() => setShowNewModal("ta")}
                  >
                    + TA Contract
                  </button>
                  
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--primary-color)" }}
                    onClick={() => setShowIntakeAssessmentModal(true)}
                  >
                    + Intake Assessment
                  </button>
                  
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--primary-color)" }}
                    onClick={() => setShowHandwrittenNoteModal(true)}
                  >
                    <span style={enhancedStyles.buttonIcon}>✏️</span> Handwritten Note
                  </button>
                </div>
              </div>
              
              {/* Second action buttons row - Administrative Documents */}
              <div style={enhancedStyles.actionCategory}>
                <h3 style={enhancedStyles.actionCategoryTitle}>Administrative Documents</h3>
                <div style={enhancedStyles.actionButtonsRow}>
                  {/* Generate Intake Link Button */}
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--accent-color)" }}
                    onClick={openGenerateLinkModal}
                  >
                    <span style={enhancedStyles.buttonIcon}>📋</span> Generate Intake Link
                  </button>
                  
                  {/* ROI Form Button */}
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--accent-color)" }}
                    onClick={openGenerateROIModal}
                  >
                    <span style={enhancedStyles.buttonIcon}>📄</span> Release of Information
                  </button>
                  
                  {/* Collateral Contact Button */}
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--accent-color)" }}
                    onClick={() => setShowCollateralContactModal(true)}
                  >
                    <span style={enhancedStyles.buttonIcon}>📞</span> Collateral Contact
                  </button>
                  
                  {/* School Note Button */}
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--accent-color)" }}
                    onClick={() => setShowSchoolNoteModal(true)}
                  >
                    <span style={enhancedStyles.buttonIcon}>🏫</span> School Note
                  </button>

                  {/* Send Welcome Email Button */}
                  <button
                    style={{ ...enhancedStyles.primaryButton, backgroundColor: "var(--accent-color)" }}
                    onClick={handleOpenEmailModal}
                  >
                    <span style={enhancedStyles.buttonIcon}>✉️</span> Send Welcome Email
                  </button>
                </div>
              </div>

              {/* CLIENT FORMS SECTION - Enhanced with Generate Link button */}
              <div style={enhancedStyles.formsSection}>
                <div style={enhancedStyles.sectionTitleWithButton}>
                  <h3 style={enhancedStyles.sectionTitle}>Client Forms</h3>
                  <button
                    style={enhancedStyles.generateLinkButton}
                    onClick={openGenerateLinkModal}
                  >
                    + Generate Intake Link
                  </button>
                </div>
                
                {clientForms.length > 0 ? (
                  <div style={enhancedStyles.formButtonContainer}>
                    {clientForms.map((form) => {
                      const formType = determineFormType(form);
                      const formDate = formatDate(form.created_at);
                      
                      return (
                        <button
                          key={form.id}
                          style={enhancedStyles.formButton}
                          onClick={() => handleViewForm(form)}
                        >
                          {formType} - {formDate}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p style={enhancedStyles.noFormsMessage}>No forms submitted yet.</p>
                )}
              </div>

              {/* ROI FORMS SECTION */}
              <div style={enhancedStyles.formsSection}>
                <div style={enhancedStyles.sectionTitleWithButton}>
                  <h3 style={enhancedStyles.sectionTitle}>Release of Information</h3>
                  <button
                    style={enhancedStyles.generateLinkButton}
                    onClick={openGenerateROIModal}
                  >
                    + Generate ROI Form
                  </button>
                </div>
                
                {roiSubmissions.length > 0 ? (
                  <div style={enhancedStyles.formButtonContainer}>
                    {roiSubmissions.map((submission) => (
                      <button
                        key={submission.id}
                        style={enhancedStyles.formButton}
                        onClick={() => handleViewROISubmission(submission)}
                      >
                        ROI: {submission.authorize_release_to} - {new Date(submission.created_at).toLocaleDateString()}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p style={enhancedStyles.noFormsMessage}>No ROI forms submitted yet.</p>
                )}
              </div>

              {/* SCHOOL CONSENT SECTION */}
              <div style={enhancedStyles.formsSection}>
                <div style={enhancedStyles.sectionTitleWithButton}>
                  <h3 style={enhancedStyles.sectionTitle}>School Consent</h3>
                  <button
                    style={enhancedStyles.generateLinkButton}
                    onClick={openSendSchoolConsentModal}
                  >
                    + Send School Consent Form
                  </button>
                </div>

                {schoolConsentSubmissions.length > 0 ? (
                  <div style={enhancedStyles.formButtonContainer}>
                    {schoolConsentSubmissions.map((submission) => (
                      <button
                        key={submission.id}
                        style={enhancedStyles.formButton}
                        onClick={() => handleViewSchoolConsent(submission)}
                      >
                        School Consent: {submission.school_name} - {new Date(submission.created_at).toLocaleDateString()}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p style={enhancedStyles.noFormsMessage}>No school consent forms submitted yet.</p>
                )}
              </div>

              <h3 style={enhancedStyles.noteListTitle}>Running Record</h3>
              {clientNotes.length === 0 && !loading && (
                <p style={enhancedStyles.noNotesMessage}>No notes yet.</p>
              )}
              <ul style={enhancedStyles.noteList}>
                {clientNotes.map((note) => {
                  let parsed = {};
                  try {
                    parsed = JSON.parse(note.notes);
                  } catch {}
                  
                  // Determine note type and display info
                  const noteType = getNoteType(note);
                  const displayDate = parsed.date || new Date(note.created_at).toLocaleDateString();
                  
                  let displayTitle = displayDate;
                  let displayStyle = {};
                  let typeIcon = "";
                  
                  if (noteType === "TA Contract") {
                    displayTitle = `${displayDate} - TA Contract`;
                    displayStyle = { borderLeft: "3px solid var(--success-color)" };
                    typeIcon = "📝";
                  } else if (noteType === "IntakeAssessment") {
                    displayTitle = `${displayDate} - Intake Assessment`;
                    displayStyle = { borderLeft: "3px solid #6f42c1" };
                    typeIcon = "📋";
                  } else if (noteType === "HandwrittenNote") {
                    displayTitle = `${displayDate} - ${parsed.title || "Handwritten Note"}`;
                    displayStyle = { borderLeft: "3px solid #e83e8c" };
                    typeIcon = "✏️";
                  } else if (noteType === "CollateralContact") {
                    displayTitle = `${displayDate} - ${parsed.contactType || "Collateral Contact"}`;
                    displayStyle = { borderLeft: "3px solid var(--primary-color)" };
                    typeIcon = "📞";
                  } else if (noteType === "SchoolNote") {
                    displayTitle = `${displayDate} - School Note`;
                    displayStyle = { borderLeft: "3px solid #17a2b8" };
                    typeIcon = "🏫";
                  } else {
                    displayStyle = { borderLeft: "3px solid var(--primary-color)" };
                    typeIcon = "📒";
                  }
                  
                  return (
                    <li
                      key={note.id}
                      style={{...enhancedStyles.noteItem, ...displayStyle}}
                      onClick={() => viewNote(note)}
                    >
                      <div style={enhancedStyles.noteTypeIcon}>{typeIcon}</div>
                      <div style={enhancedStyles.noteContent}>
                        <div style={enhancedStyles.noteTitle}>
                          <strong>{displayTitle}</strong>
                        </div>
                        <div style={enhancedStyles.noteMeta}>
                          Created: {new Date(note.created_at).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <div style={enhancedStyles.noClientSelectedState}>
              <div style={enhancedStyles.noClientIcon}>👈</div>
              <p style={enhancedStyles.noClientMessage}>Select a client to view their records</p>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Modals for creating new notes */}
      {showNewModal === "soap" && (
        <SOAPNewModal
          isOpen={showNewModal === "soap"}
          onClose={() => setShowNewModal(false)}
          onSubmit={createNote}
          clientId={selectedClient?.id}
          clientName={selectedClient?.client_name}
        />
      )}

      {showNewModal === "ta" && (
        <TAContractModal
          isOpen={showNewModal === "ta"}
          onClose={() => setShowNewModal(false)}
          onSubmit={createNote}
          clientId={selectedClient?.id}
          clientName={selectedClient?.client_name}
        />
      )}
      
      {/* New Intake Assessment Modal */}
      <IntakeAssessmentModal
        isOpen={showIntakeAssessmentModal}
        onClose={() => setShowIntakeAssessmentModal(false)}
        onSubmit={createNote}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
      />
      
      {/* New Handwritten Note Modal */}
      <HandwrittenNoteModal
        isOpen={showHandwrittenNoteModal}
        onClose={() => setShowHandwrittenNoteModal(false)}
        onSubmit={createNote}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
      />
      
      {/* New Collateral Contact Modal */}
      <CollateralContactModal
        isOpen={showCollateralContactModal}
        onClose={() => setShowCollateralContactModal(false)}
        onSubmit={createNote}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
      />
      
      {/* New School Note Modal */}
      <SchoolNoteModal
        isOpen={showSchoolNoteModal}
        onClose={() => setShowSchoolNoteModal(false)}
        onSubmit={createNote}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
      />

      {/* Modal for viewing/editing existing note */}
      {viewingNote && (
        <>
          {getNoteType(viewingNote) === "TA Contract" && (
            <TAContractViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}

          {getNoteType(viewingNote) === "IntakeAssessment" && (
            <IntakeAssessmentViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}

          {getNoteType(viewingNote) === "HandwrittenNote" && (
            <HandwrittenNoteViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}

          {getNoteType(viewingNote) === "CollateralContact" && (
            <CollateralContactViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}

          {getNoteType(viewingNote) === "SchoolNote" && (
            <SchoolNoteViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}

          {getNoteType(viewingNote) !== "TA Contract" &&
           getNoteType(viewingNote) !== "IntakeAssessment" &&
           getNoteType(viewingNote) !== "HandwrittenNote" &&
           getNoteType(viewingNote) !== "CollateralContact" &&
           getNoteType(viewingNote) !== "SchoolNote" && (
            <SOAPViewModal
              isOpen={true}
              note={viewingNote}
              onClose={() => setViewingNote(null)}
              onUpdate={updateSOAPNote}
              onSendAsPDF={handleSendAsPDF}
            />
          )}
        </>
      )}

      {/* Modal for viewing/editing client profile */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={closeProfileModal}
        clientData={selectedClientDetails}
        onSave={handleSaveProfile}
      />
      
      {/* Modal for viewing client forms */}
      <ClientFormModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setSelectedForm(null);
        }}
        formData={selectedForm}
      />
      
      {/* Modal for generating intake link */}
      <GenerateIntakeLinkModal
        isOpen={showGenerateLinkModal}
        onClose={closeGenerateLinkModal}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
      />

      {/* Modal for generating ROI form */}
      <GenerateROIModal
        isOpen={showGenerateROIModal}
        onClose={closeGenerateROIModal}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
        clientEmail={selectedClientDetails?.email}
      />

      {/* Modal for viewing ROI submissions */}
      <ViewROISubmissionModal
        isOpen={showViewROIModal}
        onClose={closeViewROIModal}
        submission={selectedROISubmission}
        onSendAsPDF={handleSendROIAsPDF}
      />

      {/* Modal for sending the school consent form link */}
      <SendSchoolConsentModal
        isOpen={showSendSchoolConsentModal}
        onClose={closeSendSchoolConsentModal}
        clientId={selectedClient?.id}
        clientName={selectedClient?.client_name}
        clientEmail={selectedClientDetails?.email}
      />

      {/* Modal for viewing a school consent submission */}
      <ViewSchoolConsentModal
        isOpen={showViewSchoolConsentModal}
        onClose={closeViewSchoolConsentModal}
        submission={selectedSchoolConsent}
        onSendAsPDF={handleSendSchoolConsentAsPDF}
      />

      {/* Send Note / ROI / School Consent as PDF — with preview */}
      {showPdfEmailModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10001, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", width: "860px", maxWidth: "96vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 6px 28px rgba(0,0,0,0.25)" }}>
            <div style={{ background: "var(--primary-color)", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <h2 style={{ margin: 0, color: "#fff", fontSize: "17px", fontWeight: 600 }}>Send {pdfEmailNoteType} as PDF</h2>
              <button onClick={() => { setShowPdfEmailModal(false); setPdfEmailStatus(null); }} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#fff", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>

            {pdfEmailStatus === "success" ? (
              <div>
                <p style={{ color: "#155724", background: "#d4edda", padding: "12px", borderRadius: "4px" }}>
                  Sent successfully to <strong>{pdfEmailTo}</strong>!
                </p>
                <button onClick={() => { setShowPdfEmailModal(false); setPdfEmailStatus(null); }} style={{ marginTop: "8px", padding: "8px 18px", cursor: "pointer", borderRadius: "4px", border: "1px solid var(--border-color)" }}>Close</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>

                {/* Left — compose */}
                <div style={{ flex: "0 0 280px", minWidth: "240px" }}>
                  <div style={{ marginBottom: "14px" }}>
                    <label style={{ fontWeight: "600", display: "block", marginBottom: "4px" }}>To (Email)</label>
                    <input
                      type="email"
                      value={pdfEmailTo}
                      onChange={e => setPdfEmailTo(e.target.value)}
                      style={{ width: "100%", padding: "8px", border: "1px solid var(--border-color)", borderRadius: "4px", fontSize: "14px", boxSizing: "border-box" }}
                      placeholder="recipient@example.com"
                    />
                  </div>
                  <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "0 0 16px" }}>
                    The {pdfEmailNoteType} for <strong>{selectedClient?.client_name}</strong> will be attached as a PDF.
                  </p>
                  {pdfEmailStatus === "error" && (
                    <p style={{ color: "#721c24", background: "#f8d7da", padding: "10px", borderRadius: "4px", marginBottom: "12px" }}>Failed to send. Please try again.</p>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <button
                      onClick={handleSubmitPdfEmail}
                      disabled={pdfEmailSending}
                      style={{ backgroundColor: "var(--primary-color)", color: "#fff", border: "none", borderRadius: "4px", padding: "10px 20px", cursor: "pointer", fontSize: "14px", opacity: pdfEmailSending ? 0.7 : 1 }}
                    >
                      {pdfEmailSending ? "Sending..." : "✉️ Send PDF"}
                    </button>
                    <button
                      onClick={() => { setShowPdfEmailModal(false); setPdfEmailStatus(null); }}
                      disabled={pdfEmailSending}
                      style={{ backgroundColor: "var(--text-muted)", color: "#fff", border: "none", borderRadius: "4px", padding: "10px 20px", cursor: "pointer", fontSize: "14px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Right — email preview */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "13px", color: "var(--text-strong)" }}>
                    Preview — what the recipient will receive
                  </label>
                  <div style={{ border: "1px solid var(--border-color)", borderRadius: "6px", overflow: "hidden", fontSize: "13px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                    <div style={{ background: "var(--primary-color)", padding: "16px", textAlign: "center" }}>
                      <div style={{ color: "#fff", fontSize: "15px", fontWeight: "bold" }}>Avenues Unlimited Counseling Center</div>
                    </div>
                    <div style={{ padding: "20px", background: "#fff" }}>
                      <p style={{ margin: "0 0 10px" }}>
                        Please find the attached <strong>{pdfEmailNoteType}</strong> for <strong>{selectedClient?.client_name || "your client"}</strong>.
                      </p>
                      <div style={{ border: "1px solid var(--border-color)", borderRadius: "4px", padding: "10px 14px", background: "var(--surface-muted)", display: "inline-flex", alignItems: "center", gap: "8px", margin: "10px 0" }}>
                        <span style={{ fontSize: "20px" }}>📎</span>
                        <span style={{ color: "var(--primary-color)", fontSize: "13px", fontWeight: "500" }}>
                          {pdfEmailNoteType.replace(/\s/g, '_')}_{(selectedClient?.client_name || 'Client').replace(/[,\s]+/g, '_')}.pdf
                        </span>
                      </div>
                      <p style={{ fontSize: "11px", color: "#999", margin: "10px 0 0" }}>
                        Sent securely from Avenues Unlimited Counseling Center portal.
                      </p>
                    </div>
                    <div style={{ background: "var(--surface-muted)", padding: "10px", textAlign: "center", fontSize: "11px", color: "#999" }}>
                      Avenues Unlimited Counseling Center &bull; 1774 Mellwood Ave, Louisville, KY 40206 &bull; 502.459.9635
                    </div>
                  </div>
                </div>

              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Email Modal */}
      {showEmailModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000,
          display: "flex", justifyContent: "center", alignItems: "center"
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "8px", padding: "24px",
            width: "520px", maxWidth: "95%", boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
          }}>
            <h2 style={{ marginTop: 0 }}>Send Welcome Email</h2>
            <p style={{ color: "var(--text-strong)", fontSize: "0.9rem" }}>
              Sending to: <strong>{selectedClient?.client_name}</strong>
            </p>

            {emailStatus ? (
              <div>
                <p style={{
                  padding: "12px", borderRadius: "4px",
                  backgroundColor: emailStatus.type === "success" ? "#d4edda" : "#f8d7da",
                  color: emailStatus.type === "success" ? "#155724" : "#721c24"
                }}>
                  {emailStatus.text}
                </p>
                <button
                  onClick={() => setShowEmailModal(false)}
                  style={{ marginTop: "8px", padding: "8px 16px", cursor: "pointer" }}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontWeight: "600" }}>To (Email):</label>
                  <br />
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    style={{ width: "100%", padding: "8px", marginTop: "4px", fontSize: "14px", border: "1px solid var(--border-color)", borderRadius: "4px" }}
                  />
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ fontWeight: "600" }}>Message:</label>
                  <br />
                  <textarea
                    rows={7}
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    style={{ width: "100%", padding: "8px", marginTop: "4px", fontSize: "14px", border: "1px solid var(--border-color)", borderRadius: "4px" }}
                    placeholder="Your welcome message..."
                  />
                </div>
                <p style={{ fontSize: "0.8rem", color: "#777", marginBottom: "1rem" }}>
                  An intake form link will be automatically included in the email.
                </p>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={handleSendWelcomeEmail}
                    disabled={emailSending}
                    style={{
                      backgroundColor: "var(--accent-color)", color: "#fff", border: "none",
                      borderRadius: "4px", padding: "10px 20px", cursor: "pointer",
                      fontSize: "14px", opacity: emailSending ? 0.7 : 1
                    }}
                  >
                    {emailSending ? "Sending..." : "Send Email"}
                  </button>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    disabled={emailSending}
                    style={{
                      backgroundColor: "var(--text-muted)", color: "#fff", border: "none",
                      borderRadius: "4px", padding: "10px 20px", cursor: "pointer", fontSize: "14px"
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Enhanced styles for a more polished look
const enhancedStyles = {
  pageContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'var(--surface-muted)',
    minHeight: '100vh',
    color: 'var(--text-strong)'
  },
  pageTitle: {
    fontSize: '26px',
    fontWeight: '600',
    color: 'var(--primary-color)',
    marginBottom: '20px',
    borderBottom: '2px solid var(--border-color)',
    paddingBottom: '10px'
  },
  layout: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    minHeight: 'calc(100vh - 130px)'
  },
  
  // Loading spinner
  loadingOverlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: '1000'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '10px'
  },
  
  // Left panel styles
  leftPanel: {
    width: '300px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  panelTitle: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0',
    padding: '16px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--surface-muted)'
  },
  searchContainer: {
    position: 'relative',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)'
  },
  searchBox: {
    width: '100%',
    padding: '8px 30px 8px 10px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    outline: 'none',
    transition: 'border-color 0.2s ease-in-out',
    '&:focus': {
      borderColor: '#4d90fe'
    }
  },
  searchIcon: {
    position: 'absolute',
    right: '25px',
    top: '21px',
    color: '#999',
    pointerEvents: 'none'
  },
  clientList: {
    flex: '1',
    overflowY: 'auto',
    padding: '8px 0'
  },
  clientItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    fontSize: '14px',
    borderBottom: '1px solid var(--surface-muted)',
    '&:hover': {
      backgroundColor: '#f5f8ff'
    }
  },
  emptyState: {
    padding: '20px',
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic',
    fontSize: '14px'
  },
  
  // Right panel styles
  rightPanel: {
    flex: '1',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    overflow: 'auto',
    padding: '0',
    display: 'flex',
    flexDirection: 'column'
  },
  backToClientsButton: {
    display: 'block',
    width: 'calc(100% - 24px)',
    margin: '12px',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--primary-color)',
    backgroundColor: '#E2EEFF',
    border: '1px solid #c7dcff',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left'
  },
  clientHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: 'var(--surface-muted)'
  },
  clientName: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0',
    color: 'var(--primary-color)'
  },
  
  // Client info box styles
  clientInfoBox: {
    margin: '15px 20px',
    padding: '15px',
    backgroundColor: 'var(--surface-muted)',
    borderRadius: '6px',
    border: '1px solid var(--border-color)'
  },
  clientInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '15px'
  },
  clientInfoSection: {
    backgroundColor: '#fff',
    padding: '15px',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
  },
  sectionHeading: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 12px 0',
    color: '#495057',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--border-color)'
  },
  infoRow: {
    display: 'flex',
    marginBottom: '8px',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  infoLabel: {
    width: '40%',
    fontWeight: '600',
    color: 'var(--text-muted)'
  },
  infoValue: {
    width: '60%',
    color: '#212529'
  },
  ageIndicator: {
    marginLeft: '5px',
    fontSize: '13px',
    color: 'var(--text-muted)',
    fontStyle: 'italic'
  },
  
  // Medications section
  medicationsSection: {
    backgroundColor: '#fff',
    padding: '15px',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    marginBottom: '15px'
  },
  medicationsList: {
    maxHeight: '80px',
    overflowY: 'auto',
    padding: '5px 0'
  },
  medicationsText: {
    margin: '0',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  noMedications: {
    margin: '0',
    fontSize: '14px',
    fontStyle: 'italic',
    color: 'var(--text-muted)'
  },
  
  // Profile button
  profileButtonContainer: {
    textAlign: 'right',
    marginTop: '10px'
  },
  profileButton: {
    backgroundColor: '#4b6584',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#3d5366'
    }
  },
  
  // Action Categories
  actionCategory: {
    margin: '0 20px 15px 20px'
  },
  actionCategoryTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '1px solid var(--border-color)'
  },
  
  // Action buttons
  actionButtonsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '10px'
  },
  primaryButton: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#0069d9'
    }
  },
  buttonIcon: {
    marginRight: '5px',
    fontSize: '16px'
  },
  
  // Forms section
  formsSection: {
    margin: '0 20px 20px 20px',
    padding: '15px',
    backgroundColor: 'var(--surface-muted)',
    borderRadius: '6px',
    border: '1px solid var(--border-color)'
  },
  sectionTitleWithButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
    color: '#495057'
  },
  generateLinkButton: {
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#0055a8'
    }
  },
  formButtonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },
  formButton: {
    backgroundColor: 'var(--border-color)',
    color: '#495057',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'var(--border-color)',
      borderColor: '#adb5bd'
    }
  },
  noFormsMessage: {
    fontSize: '14px',
    fontStyle: 'italic',
    color: 'var(--text-muted)',
    margin: '0'
  },
  
  // Notes list
  noteListTitle: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 20px 15px 20px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--border-color)',
    color: '#495057'
  },
  noNotesMessage: {
    margin: '0 20px 20px 20px',
    fontSize: '14px',
    fontStyle: 'italic',
    color: 'var(--text-muted)'
  },
  noteList: {
    listStyle: 'none',
    padding: '0 20px 20px 20px',
    margin: '0'
  },
  noteItem: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'var(--surface-muted)',
    marginBottom: '10px',
    borderRadius: '6px',
    padding: '12px 15px',
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)'
    }
  },
  noteTypeIcon: {
    fontSize: '20px',
    marginRight: '15px'
  },
  noteContent: {
    flex: '1'
  },
  noteTitle: {
    fontSize: '14px',
    marginBottom: '3px'
  },
  noteMeta: {
    fontSize: '12px',
    color: 'var(--text-muted)'
  },
  
  // Empty state when no client is selected
  noClientSelectedState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: '30px'
  },
  noClientIcon: {
    fontSize: '40px',
    marginBottom: '15px',
    color: '#adb5bd'
  },
  noClientMessage: {
    fontSize: '16px',
    color: 'var(--text-muted)',
    textAlign: 'center'
  }
};

export default TherapistSOAPNotesPage;